import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { useSelector } from 'react-redux';
import { isMessageModalExpandedSelector } from '../../redux/appConfigurator/selectors';

interface MessageSkeletonProps {
    align?: 'left' | 'right';
    isConnected?: boolean;
}

const MessageSkeleton: React.FC<MessageSkeletonProps> = ({ align = 'left', isConnected = false }) => {
    const { themeStyle } = useThemeContext();
    const isExpanded = useSelector(isMessageModalExpandedSelector);

    // Get border radius based on connection status
    const getBubbleBorderRadius = () => {
        if (align === 'right') {
            return isConnected ? '4px 4px 0px 16px' : '16px 16px 0px 16px';
        }
        return isConnected ? '4px 4px 16px 0px' : '16px 16px 16px 0px';
    };

    // Get random width for message content
    const getRandomWidth = () => {
        if (isExpanded) {
            // Generate wider messages more often
            const baseWidth = Math.random() * 200 + 150; // Between 150-350px
            return Math.min(baseWidth, 350); // Cap at 350px}
        } else {
            const baseWidth = Math.random() * 150 + 50; // Between 50-200px
            return Math.min(baseWidth, 200); // Cap at 200px
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
                mb: isConnected ? 0.5 : 2, // Smaller margin for connected messages
                width: '100%',
            }}>
            {align === 'left' && !isConnected && (
                <Box sx={{ mr: 1, alignSelf: 'flex-end' }}>
                    <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: theme[themeStyle].deep_grey }} />
                </Box>
            )}
            {/* Space holder for alignment when no avatar is shown */}
            {align === 'left' && isConnected && <Box sx={{ width: 40, mr: 1 }} />}

            <Box sx={{ maxWidth: { xs: '80%', sm: '70%' } }}>
                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: getBubbleBorderRadius(),
                        p: 2,
                        position: 'relative',
                    }}>
                    <Skeleton
                        variant="text"
                        width={getRandomWidth()}
                        height={20}
                        sx={{ bgcolor: theme[themeStyle].grey + '40' }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Skeleton
                            variant="text"
                            width={50}
                            height={16}
                            sx={{ bgcolor: theme[themeStyle].grey + '40' }}
                        />
                    </Box>
                </Box>
            </Box>

            {align === 'right' && !isConnected && (
                <Box sx={{ ml: 1, alignSelf: 'flex-end' }}>
                    <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: theme[themeStyle].deep_grey }} />
                </Box>
            )}
            {/* Space holder for alignment when no avatar is shown */}
            {align === 'right' && isConnected && <Box sx={{ width: 40, ml: 1 }} />}
        </Box>
    );
};

export { MessageSkeleton };
